// Capability (AIAgent_Capabilities) sub-entity client — restores the "Capabilities" Agent tab
// dropped in the 2026-08-19/20 AgentEditor.tsx refactor. Targets the real live backend
// (BizFirst.Ai.AiAgent.Api.Base's BaseCapabilityController), verified by reading the controller
// and entity directly — this is Phase 3 of the restoration (see Phase 2's Templates/Rules/Hooks
// clients for the established pattern this one follows).
//
// Route confirmed as briefed: `[Route("api/v1/ai-agent/capabilities")]` on
// BaseCapabilityController.cs — `/capabilities`, not `/agent-capabilities`.
//
// Fields: briefed shape (Description not CapabilityDescription; required CapabilityTypeID; no
// IsEnabled/CapabilityVersion; Parameters/Prerequisites JSON/Cost/IsAdvertised) all confirmed by
// reading Capability.cs directly. One field the briefing never mentioned at all: `CapabilityName`
// (required, string, 200) — the entity's actual display-name column. Documented in
// DevelopmentHistoryLog.md.
//
// getCapabilitiesByAgent posts to `by-agent` (confirmed on BaseCapabilityController.cs — a real
// route exists here, unlike PaymentRule's confirmed gap), same `{ AgentID: { ID } }` shape as
// every other by-agent query in this package.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { CapabilityDTO, CapabilityFormValues } from '../types';

export class AgentCapabilityAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  async getCapabilitiesByAgent(agentId: number): Promise<AdminApiResult<CapabilityDTO[]>> {
    const result = await this.postQuery<CapabilityDTO[]>('/api/v1/ai-agent/capabilities/by-agent', {
      AgentID: { ID: agentId },
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async createCapability(agentId: number, capability: CapabilityFormValues): Promise<AdminApiResult<CapabilityDTO>> {
    return this.post<CapabilityDTO>('/api/v1/ai-agent/capabilities', { AgentID: agentId, ...capability });
  }

  async updateCapability(capabilityId: number, capability: Partial<CapabilityFormValues>): Promise<AdminApiResult<CapabilityDTO>> {
    return this.put<CapabilityDTO>(`/api/v1/ai-agent/capabilities/${capabilityId}`, {
      CapabilityID: capabilityId,
      ...capability,
    });
  }

  async deleteCapability(capabilityId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-agent/capabilities/${capabilityId}`, { id: capabilityId });
  }
}
